import { exists } from "@examspark/common-util";
import { SearchAggsInput } from "@examspark/types";
import {
  ArraySearchFields,
  SearchFields,
} from "@examspark/types/dist/components/activity-editor";

import { SearchState } from "../context";
import { DEFAULT_AGG_SIZE } from "../useSearch";

export const buildAggs = (params: {
  searchState: SearchState;
  aggs: SearchAggsInput[];
  objectiveAuthorityIds: string[];
}) => {
  const { aggs, searchState, objectiveAuthorityIds } = params;
  const aggsNew: SearchAggsInput[] = [];

  for (const agg of aggs) {
    // collect all filters besides the current one
    const siblingAggFilters = aggs
      .filter((_agg) => _agg.name !== agg.name)
      .map((_agg) => {
        //
        // For tag facets, the name will be "tag:<tag name>"
        // otherwise the name is just a name
        //
        const [name, tagKey] = _agg.name.split(":");

        type FilterKey = keyof ArraySearchFields | "tag";

        const values = searchState.filter?.[name as FilterKey]
          ?.filter((value) => typeof value === "string" || value.key === tagKey)
          .map((value) => (typeof value === "string" ? value : value.value));

        switch (name) {
          case "alignmentIds":
          case "bankId":
          case "blooms":
          case "dok":
          case "deliveryMediaType":
          case "grade":
          case "interaction":
          case "language":
          case "maturity":
          case "objectiveScope":
          case "sk":
          case "subject":
          case "type":
          case "workspaceId":
            return values?.length ? getEsAggQuery(_agg, values ?? []) : null;
          case "tag":
            if (!values?.length) {
              return null;
            }
            return {
              query: {
                nested: {
                  path: "tags",
                  query: {
                    bool: {
                      minimum_should_match: 1,
                      should:
                        searchState.filter.tag
                          ?.filter((t) => t.key === tagKey)
                          .map((t) => ({
                            match: {
                              "tags.value.keyword": t.value,
                            },
                          })) ?? [],
                    },
                  },
                },
              },
            };
          case "referencesIdsUnit": {
            if (!values?.length) {
              return null;
            }

            return {
              query: {
                nested: {
                  path: "referencesIds",
                  query: {
                    bool: {
                      minimum_should_match: 1,
                      should: values.map((id) => ({
                        match: {
                          "referencesIds.id.keyword": id,
                        },
                      })),
                    },
                  },
                },
              },
            };
          }

          default:
            return null;
        }
      })
      .filter(exists);

    //
    // For tag facets, the name will be "tag:<tag name>"
    // otherwise the name is just a name
    //
    const [name, tagKey] = agg.name.split(":");

    switch (name) {
      case "alignmentIds":
      case "bankId":
      case "blooms":
      case "dok":
      case "deliveryMediaType":
      case "grade":
      case "interaction":
      case "language":
      case "maturity":
      case "subject":
      case "sk":
      case "type":
      case "workspaceId":
        aggsNew.push(
          getEsFilterAggQuery(agg.name, agg.size, siblingAggFilters),
        );
        break;
      case "tag":
        if (tagKey == null) {
          // @todo better message
          throw new Error("This is bad");
        }
        aggsNew.push({
          name: agg.name,
          type: "filter",
          query: !siblingAggFilters.length
            ? ES_MATCH_ALL
            : JSON.stringify({
                bool: {
                  must: siblingAggFilters.map((f) => f.query),
                },
              }),
          aggs: [
            {
              name: agg.name,
              type: "nested",
              path: "tags",
              aggs: [
                {
                  name: agg.name,
                  type: "filter",
                  query: JSON.stringify({
                    bool: {
                      must: [
                        {
                          match: {
                            "tags.key.keyword": tagKey,
                          },
                        },
                      ],
                    },
                  }),
                  aggs: [
                    {
                      name: agg.name,
                      type: "terms",
                      size: 15,
                      field: "tags.value",
                    },
                  ],
                },
              ],
            },
          ],
        });
        break;
      case "referencesIdsUnit":
        aggsNew.push({
          name: "referencesIdsUnit",
          type: "filter",
          query: !siblingAggFilters.length
            ? ES_MATCH_ALL
            : JSON.stringify({
                bool: {
                  must: siblingAggFilters.map((f) => f.query),
                },
              }),
          aggs: [
            {
              name: "referencesIdsUnit",
              type: "nested",
              path: "referencesIds",
              aggs: [
                {
                  name: "referencesIdsUnit",
                  type: "filter",
                  size: 50,
                  query: JSON.stringify({
                    bool: {
                      minimum_should_match: 1,
                      should:
                        searchState.filter.referencesIdsUnitCourseUnits?.map(
                          (unitId) => {
                            return {
                              match: {
                                "referencesIds.id.keyword": unitId,
                              },
                            };
                          },
                        ) ?? [],
                    },
                  }),
                  aggs: [agg],
                },
              ],
            },
          ],
        });
        // }
        break;
      case "objectiveScope":
        // this special case needs to only show objectiveAuthorityIds where
        // this preference is set
        aggsNew.push(
          getEsFilterAggQuery(
            agg.name,
            agg.size,
            siblingAggFilters,
            getEsTermsAggIncludeString(objectiveAuthorityIds),
          ),
        );
        break;
      default:
        aggsNew.push(agg);
        break;
    }
  }

  return aggsNew;
};

const getEsTermsAggIncludeString = (values: string[]) => {
  return values.map((id) => `${id}.*`).join("|");
};

const getEsFilterAggQuery = (
  name: string,
  size: number | null | undefined,
  siblingFilters: {
    query?: any;
    nested?: any;
  }[],
  currentAggFilter?: string,
): SearchAggsInput => {
  const agg: SearchAggsInput = {
    name: name,
    type: "filter",
    query: !siblingFilters.length
      ? ES_MATCH_ALL
      : JSON.stringify({
          bool: {
            must: siblingFilters.map((f) => f.query),
          },
        }),
    aggs: [getEsTermsAggQuery(name, size, currentAggFilter)],
  };

  return agg;
};

const getEsTermsAggQuery = (
  name: string,
  size: number | null | undefined,
  filter?: string,
) => {
  const agg: SearchAggsInput = {
    name: name,
    field: name,
    type: "terms",
    size: size ?? DEFAULT_AGG_SIZE,
  };

  if (filter) agg.include = filter;
  return agg;
};

const getEsAggQuery = (agg: SearchAggsInput, values: string[]) => {
  const name = agg.field as keyof SearchFields;
  return {
    query: {
      bool: {
        minimum_should_match: 1,
        should: values.map((id) => ({
          match: {
            [`${name}.keyword`]: id,
          },
        })),
      },
    },
  };
};

const ES_MATCH_ALL = '{"bool":{"must":[{"match_all":{}}]}}';
